import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useLocation } from 'react-router-dom';
import api from '../services/api';

/** Default cadence when the server has not returned a value yet (5 minutes). */
export const DEFAULT_MONITORING_LIVE_POLL_INTERVAL_MS = 5 * 60 * 1000;
/** @deprecated Use `DEFAULT_MONITORING_LIVE_POLL_INTERVAL_MS` or `useGlobalMonitoringAlerts().livePollIntervalMs`. */
export const MONITORING_LIVE_POLL_INTERVAL_MS = DEFAULT_MONITORING_LIVE_POLL_INTERVAL_MS;

export const MIN_MONITORING_LIVE_POLL_INTERVAL_MS = 60 * 1000;
export const MAX_MONITORING_LIVE_POLL_INTERVAL_MS = 60 * 60 * 1000;

export function clampLivePollIntervalMs(ms: number): number {
  if (!Number.isFinite(ms)) return DEFAULT_MONITORING_LIVE_POLL_INTERVAL_MS;
  return Math.min(
    MAX_MONITORING_LIVE_POLL_INTERVAL_MS,
    Math.max(MIN_MONITORING_LIVE_POLL_INTERVAL_MS, Math.round(ms))
  );
}

export type MonitoringAlertThresholds = {
  cpuThreshold: number;
  memoryThreshold: number;
  storageThreshold: number;
  postgresCpuThreshold: number;
  postgresMemoryThreshold: number;
  networkThreshold: number;
  appLoadThreshold: number;
  /** Poll interval for global bar (off `/monitoring`) and dashboard “Auto” refresh (ms). */
  livePollIntervalMs: number;
};

const DEFAULT_ALERT_THRESHOLDS: MonitoringAlertThresholds = {
  cpuThreshold: 90,
  memoryThreshold: 90,
  storageThreshold: 90,
  postgresCpuThreshold: 90,
  postgresMemoryThreshold: 90,
  networkThreshold: 90,
  appLoadThreshold: 100,
  livePollIntervalMs: DEFAULT_MONITORING_LIVE_POLL_INTERVAL_MS,
};

const ALERT_THRESHOLDS_STORAGE_KEY = 'pulse:monitoring:alert-thresholds:v1';

const THRESHOLD_CACHE_KEYS: (keyof MonitoringAlertThresholds)[] = [
  'cpuThreshold',
  'memoryThreshold',
  'storageThreshold',
  'postgresCpuThreshold',
  'postgresMemoryThreshold',
  'networkThreshold',
  'appLoadThreshold',
  'livePollIntervalMs',
];

function readAlertThresholdsFromLocalStorage(): MonitoringAlertThresholds | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.localStorage.getItem(ALERT_THRESHOLDS_STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const o = parsed as Record<string, unknown>;
    for (const k of THRESHOLD_CACHE_KEYS) {
      const v = o[k];
      if (typeof v !== 'number' || !Number.isFinite(v)) return null;
    }
    const poll = clampLivePollIntervalMs(o.livePollIntervalMs as number);
    return {
      ...DEFAULT_ALERT_THRESHOLDS,
      ...o,
      livePollIntervalMs: poll,
    } as MonitoringAlertThresholds;
  } catch {
    return null;
  }
}

function writeAlertThresholdsToLocalStorage(data: MonitoringAlertThresholds): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(ALERT_THRESHOLDS_STORAGE_KEY, JSON.stringify(data));
  } catch {
    // Quota / private mode — polling will fall back to network when cache is missing
  }
}

type TimeSeriesPoint = { label: string; value: number };

interface PostgresLivePayload {
  configured?: boolean;
  liveAzureEnabled?: boolean;
  postgres?: {
    storageUsedPercent: number | null;
    cpuPercent?: TimeSeriesPoint[];
    memoryPercent?: TimeSeriesPoint[];
  };
}

interface ApplicationLivePayload {
  configured?: boolean;
  liveAzureEnabled?: boolean;
  application?: {
    cpuLoadPercent: TimeSeriesPoint[];
    memoryUsagePercent: TimeSeriesPoint[];
    networkLoadPercent: TimeSeriesPoint[];
    httpRequestsPerSec?: TimeSeriesPoint[];
  };
}

function isMonitoringRoute(pathname: string): boolean {
  return pathname === '/monitoring' || pathname.startsWith('/monitoring/');
}

function lastSeriesValue(series: TimeSeriesPoint[] | undefined): number | null {
  if (!series?.length) return null;
  const v = series[series.length - 1]?.value;
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

const MESSAGES = {
  storage: 'PostgreSQL storage usage has exceeded the defined threshold.',
  postgresCpu: 'PostgreSQL database CPU usage has exceeded the defined threshold.',
  postgresMemory: 'PostgreSQL database memory usage has exceeded the defined threshold.',
  cpu: 'Application CPU load is high, which may impact performance.',
  memory: 'Application memory usage is above the acceptable limit.',
  network: 'Network load is high, which may cause latency or request delays.',
  appLoad: 'HTTP request rate is above the configured application load threshold.',
} as const;

function buildThresholdAlerts(
  pg: PostgresLivePayload | null | undefined,
  app: ApplicationLivePayload | null | undefined,
  thresholds: MonitoringAlertThresholds
): string[] {
  const alerts: string[] = [];
  const p = pg ?? ({} as PostgresLivePayload);
  const a = app ?? ({} as ApplicationLivePayload);

  if (p.liveAzureEnabled && p.configured) {
    const pg = p.postgres;
    const sp = pg?.storageUsedPercent;
    if (sp != null && sp > thresholds.storageThreshold) {
      alerts.push(MESSAGES.storage);
    }
    const pgCpu = lastSeriesValue(pg?.cpuPercent);
    if (pgCpu != null && pgCpu > thresholds.postgresCpuThreshold) {
      alerts.push(MESSAGES.postgresCpu);
    }
    const pgMem = lastSeriesValue(pg?.memoryPercent);
    if (pgMem != null && pgMem > thresholds.postgresMemoryThreshold) {
      alerts.push(MESSAGES.postgresMemory);
    }
  }

  if (a.liveAzureEnabled && a.configured) {
    const appSeries = a.application;
    const cpu = lastSeriesValue(appSeries?.cpuLoadPercent);
    if (cpu != null && cpu > thresholds.cpuThreshold) alerts.push(MESSAGES.cpu);

    const mem = lastSeriesValue(appSeries?.memoryUsagePercent);
    if (mem != null && mem > thresholds.memoryThreshold) alerts.push(MESSAGES.memory);

    const net = lastSeriesValue(appSeries?.networkLoadPercent);
    if (net != null && net > thresholds.networkThreshold) alerts.push(MESSAGES.network);

    const load = lastSeriesValue(appSeries?.httpRequestsPerSec);
    if (load != null && load > thresholds.appLoadThreshold) alerts.push(MESSAGES.appLoad);
  }

  return alerts;
}

type GlobalMonitoringAlertContextValue = {
  thresholdAlerts: string[];
  dismissThresholdAlerts: () => void;
  /** Reload threshold values from the server (e.g. after saving configuration). */
  refreshAlertThresholds: () => Promise<void>;
  /** Call from Monitoring dashboard when live snapshot data changes (same APIs as charts). */
  syncMonitoringThresholdAlerts: (
    pg: PostgresLivePayload | null | undefined,
    app: ApplicationLivePayload | null | undefined
  ) => void;
  /** Configured live metrics poll interval (global bar + dashboard “Auto”), in milliseconds. */
  livePollIntervalMs: number;
};

const GlobalMonitoringAlertContext = createContext<GlobalMonitoringAlertContextValue | null>(null);

export function GlobalMonitoringAlertProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const { pathname } = useLocation();
  const [thresholdAlerts, setThresholdAlerts] = useState<string[]>([]);
  const thresholdsRef = useRef<MonitoringAlertThresholds>(DEFAULT_ALERT_THRESHOLDS);
  const [livePollIntervalMs, setLivePollIntervalMs] = useState(DEFAULT_MONITORING_LIVE_POLL_INTERVAL_MS);

  const dismissThresholdAlerts = useCallback(() => {
    setThresholdAlerts([]);
  }, []);

  /**
   * Loads threshold + poll interval. Uses `localStorage` after the first successful fetch unless
   * `bypassCache` is true (e.g. after saving Monitoring configuration).
   */
  const fetchAlertThresholds = useCallback(
    async (bypassCache = false): Promise<MonitoringAlertThresholds | null> => {
      if (!bypassCache) {
        const cached = readAlertThresholdsFromLocalStorage();
        if (cached) {
          thresholdsRef.current = cached;
          setLivePollIntervalMs(cached.livePollIntervalMs);
          return cached;
        }
      }
      try {
        const { data } = await api.get<Partial<MonitoringAlertThresholds>>('/monitoring/alert-thresholds');
        const poll = clampLivePollIntervalMs(
          typeof data.livePollIntervalMs === 'number'
            ? data.livePollIntervalMs
            : DEFAULT_MONITORING_LIVE_POLL_INTERVAL_MS
        );
        const normalized: MonitoringAlertThresholds = {
          ...DEFAULT_ALERT_THRESHOLDS,
          ...data,
          livePollIntervalMs: poll,
        };
        thresholdsRef.current = normalized;
        setLivePollIntervalMs(poll);
        writeAlertThresholdsToLocalStorage(normalized);
        return normalized;
      } catch {
        return null;
      }
    },
    []
  );

  const refreshAlertThresholds = useCallback(async () => {
    await fetchAlertThresholds(true);
    if (isMonitoringRoute(pathname)) {
      return;
    }
    try {
      const [pgRes, appRes] = await Promise.all([
        api.get<PostgresLivePayload>('/monitoring/postgresql-data', { params: { range: '1h' } }),
        api.get<ApplicationLivePayload>('/monitoring/app-monitoring-data', { params: { range: '1h' } }),
      ]);
      setThresholdAlerts(buildThresholdAlerts(pgRes.data, appRes.data, thresholdsRef.current));
    } catch {
      // ignore
    }
  }, [fetchAlertThresholds, pathname]);

  const syncMonitoringThresholdAlerts = useCallback(
    (pg: PostgresLivePayload | null | undefined, app: ApplicationLivePayload | null | undefined) => {
      setThresholdAlerts(buildThresholdAlerts(pg, app, thresholdsRef.current));
    },
    []
  );

  const value = useMemo(
    () => ({
      thresholdAlerts,
      dismissThresholdAlerts,
      refreshAlertThresholds,
      syncMonitoringThresholdAlerts,
      livePollIntervalMs,
    }),
    [
      thresholdAlerts,
      dismissThresholdAlerts,
      refreshAlertThresholds,
      syncMonitoringThresholdAlerts,
      livePollIntervalMs,
    ]
  );

  return (
    <GlobalMonitoringAlertContext.Provider value={value}>
      {children}
      <MonitoringThresholdPoller
        setThresholdAlerts={setThresholdAlerts}
        fetchAlertThresholds={fetchAlertThresholds}
        thresholdsRef={thresholdsRef}
        pollIntervalMs={livePollIntervalMs}
      />
    </GlobalMonitoringAlertContext.Provider>
  );
}

export function useGlobalMonitoringAlerts(): GlobalMonitoringAlertContextValue {
  const ctx = useContext(GlobalMonitoringAlertContext);
  if (!ctx) {
    throw new Error('useGlobalMonitoringAlerts must be used within GlobalMonitoringAlertProvider');
  }
  return ctx;
}

function MonitoringThresholdPoller({
  setThresholdAlerts,
  fetchAlertThresholds,
  thresholdsRef,
  pollIntervalMs,
}: {
  setThresholdAlerts: React.Dispatch<React.SetStateAction<string[]>>;
  fetchAlertThresholds: (bypassCache?: boolean) => Promise<MonitoringAlertThresholds | null>;
  thresholdsRef: React.MutableRefObject<MonitoringAlertThresholds>;
  pollIntervalMs: number;
}): null {
  const { pathname } = useLocation();
  const locationRef = useRef(pathname);
  locationRef.current = pathname;

  const tick = useCallback(async () => {
    await fetchAlertThresholds();

    if (isMonitoringRoute(locationRef.current)) {
      return;
    }
    try {
      const [pgRes, appRes] = await Promise.all([
        api.get<PostgresLivePayload>('/monitoring/postgresql-data', { params: { range: '1h' } }),
        api.get<ApplicationLivePayload>('/monitoring/app-monitoring-data', { params: { range: '1h' } }),
      ]);
      const alerts = buildThresholdAlerts(pgRes.data, appRes.data, thresholdsRef.current);
      setThresholdAlerts(alerts);
    } catch {
      // Ignore failures (e.g. 403, network) — do not surface false alerts
    }
  }, [fetchAlertThresholds, setThresholdAlerts, thresholdsRef]);

  const tickRef = useRef(tick);
  tickRef.current = tick;

  // Restart when the configured poll interval changes (e.g. after saving Monitoring configuration).
  useEffect(() => {
    const run = () => void tickRef.current();
    void run();
    const ms = clampLivePollIntervalMs(pollIntervalMs);
    const id = window.setInterval(run, ms);
    return () => window.clearInterval(id);
  }, [pollIntervalMs]);

  return null;
}
